import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, X } from "lucide-react";
import { type AccountIssue, ACCOUNT_ISSUES } from "@/lib/accounts";
import { CounterButton } from "@/components/ui/counter-button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/cn";
import { type AccountFilters, type FleetCounts, ALL } from "./accountFilters";

/** Issues that clear on their own are amber; the rest need a hand. */
const SELF_CLEARING: AccountIssue[] = ["flood_wait", "spam_limit"];

/**
 * Fleet state under the page title. Each number is a filter: clicking it sets
 * the same Status / Issue filter the toolbar has, and the number is exactly
 * how many rows that shows (it's counted over the other active filters).
 */
export function FleetCounters({
  counts,
  filters,
  onChange,
}: {
  counts: FleetCounts;
  filters: AccountFilters;
  onChange: (patch: Partial<AccountFilters>) => void;
}) {
  const { t } = useTranslation();
  const [issuesOpen, setIssuesOpen] = useState(false);
  const noStatusFilter = filters.status === ALL && filters.issue === ALL;
  const pick = (status: string) => onChange({ status, issue: ALL });
  const pickIssue = (issue: string) => {
    onChange({ issue, status: ALL });
    setIssuesOpen(false);
  };
  // Empty kinds stay out of the way — except the one currently applied.
  const issuesShown = ACCOUNT_ISSUES.filter((i) => counts.byIssue[i] > 0 || filters.issue === i);

  return (
    <span className="-ml-2 flex flex-wrap items-center gap-0.5">
      <CounterButton
        label={t("accounts.counters.total")}
        value={counts.total}
        selected={noStatusFilter}
        onClick={() => pick(ALL)}
      />
      <CounterButton
        label={t("accounts.counters.active")}
        dot="bg-success"
        value={counts.byStatus.active ?? 0}
        selected={filters.status === "active"}
        onClick={() => pick("active")}
      />
      <CounterButton
        label={t("accounts.counters.warming")}
        dot="bg-warning"
        value={counts.byStatus.warming_up ?? 0}
        selected={filters.status === "warming_up"}
        onClick={() => pick("warming_up")}
      />
      <Popover open={issuesOpen} onOpenChange={setIssuesOpen}>
        <PopoverTrigger asChild>
          <CounterButton
            label={t("accounts.counters.issues")}
            value={counts.issues}
            tone="danger"
            selected={filters.issue !== ALL}
            trailing={<ChevronDown size={14} strokeWidth={2} aria-hidden="true" />}
          />
        </PopoverTrigger>
        <PopoverContent className="w-72 p-1.5">
          <IssueRow
            label={t("accounts.counters.all_issues")}
            count={counts.issues}
            selected={filters.issue === "any"}
            onClick={() => pickIssue("any")}
          />
          {issuesShown.length > 0 && <div className="my-1 border-t border-border" />}
          {issuesShown.map((issue) => (
            <IssueRow
              key={issue}
              label={t(`accounts.issue.${issue}`)}
              hint={t(`accounts.issue_hint.${issue}`)}
              count={counts.byIssue[issue]}
              dot={SELF_CLEARING.includes(issue) ? "bg-warning" : "bg-danger"}
              selected={filters.issue === issue}
              onClick={() => pickIssue(issue)}
            />
          ))}
          {counts.issues === 0 && (
            <p className="px-2 py-1.5 text-xs text-fg-muted">{t("accounts.counters.no_issues")}</p>
          )}
        </PopoverContent>
      </Popover>
    </span>
  );
}

function IssueRow({
  label,
  hint,
  count,
  dot,
  selected,
  onClick,
}: {
  label: string;
  hint?: string;
  count: number;
  dot?: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex w-full items-start gap-2 rounded-md px-2 py-1.5 text-left text-sm",
        "hover:bg-bg-secondary focus-visible:bg-bg-secondary focus-visible:outline-none",
        selected && "bg-accent/15",
      )}
    >
      {dot ? (
        <span aria-hidden="true" className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", dot)} />
      ) : (
        <span aria-hidden="true" className="w-2 shrink-0" />
      )}
      <span className="min-w-0 flex-1">
        <span className="block text-fg-primary">{label}</span>
        {hint && <span className="block text-xs text-fg-muted">{hint}</span>}
      </span>
      <span className="font-semibold tabular-nums text-fg-primary">{count}</span>
    </button>
  );
}

export interface FilterChip {
  key: keyof AccountFilters;
  label: string;
  value: string;
}

/** "Showing 18 of 120" + one removable chip per active filter + Clear all. */
export function ActiveFilterChips({
  chips,
  shown,
  total,
  onRemove,
  onClearAll,
}: {
  chips: FilterChip[];
  shown: number;
  total: number;
  onRemove: (key: keyof AccountFilters) => void;
  onClearAll: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-fg-muted">{t("accounts.filter.showing", { shown, count: total })}</span>
      {chips.map((c) => (
        <span
          key={c.key}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-bg-secondary py-0.5 pl-2 pr-1 text-xs"
        >
          <span className="text-fg-muted">{c.label}</span>
          <span className="max-w-[12rem] truncate text-fg-primary">{c.value}</span>
          <button
            type="button"
            onClick={() => onRemove(c.key)}
            aria-label={t("accounts.filter.remove", { name: c.label })}
            className="rounded p-0.5 text-fg-muted hover:bg-bg-elevated hover:text-fg-primary"
          >
            <X size={12} strokeWidth={2} aria-hidden="true" />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onClearAll}
        className="text-xs font-medium text-accent underline-offset-4 hover:underline"
      >
        {t("accounts.filter.clear_all")}
      </button>
    </div>
  );
}
